// Playtest stats in the game (2026-10-31): what to send and when. The event shape and every privacy rule live in
// src/stats/schema.ts; main.ts only calls the hooks below. Events wait in a short queue and go as one beacon (text/plain,
// so no preflight) when ten are waiting, when the page is hidden or left, and right after a match ends. Nothing is
// stored on the device, so a reload is a stranger.
import { MAX_EVENTS, STATS_PATH, clean, fpsBucket, type EventName, type QuitAt, type Via } from '../stats/schema';
import type { Screen } from './ui/flow';

/** Browser notices that reach window.onerror but are not faults (sheep-tag analytics.ts BENIGN): a resize callback that
 *  changed layout, and the opaque "Script error." a cross-origin script gives. */
const BENIGN = [/^ResizeObserver loop/, /^Script error\.?$/];

export class PlayStats {
  private queue: { name: EventName; props: Record<string, string | number> }[] = [];
  private errors = new Set<string>();
  private at: QuitAt = 'title';
  /** This match: played by you, already ended (its end and fps sent), the frame rates seen while it ran. */
  private match = { on: false, ended: false, fps: [] as number[] };
  private left = false;

  constructor(private send: (body: string) => void, private test: boolean, private input: 'touch' | 'mouse') {}

  track(name: EventName, props: Record<string, string | number> = {}) {
    const e = clean({ name, props: this.test ? { ...props, test: 1 } : props });
    if (!e) return;
    this.queue.push(e);
    if (this.queue.length >= 10) this.flush();
  }
  flush() {
    while (this.queue.length) {
      try { this.send(JSON.stringify({ events: this.queue.splice(0, MAX_EVENTS) })); } catch { /* stats never break the game */ }
    }
  }

  /** The page opened, and which button brought it here (schema.ts viaOf). */
  visit(via: Via) { this.track('visit', { input: this.input, via }); }
  /** A fresh match starts: `played` when it is yours (not watching bots). */
  start(played: boolean) { this.match = { on: played, ended: false, fps: [] }; }
  /** The screen changed; `played`: the match on screen is yours (pause counts as the match). */
  screen(s: Screen, played: boolean) {
    this.at = s === 'title' ? 'title' : s === 'hero' ? 'hero_pick' : s === 'pick' ? 'army_pick' : s === 'end' ? 'end'
      : played ? 'match' : 'watch';
  }
  /** Back to the title, before the match is thrown away (`min`: its minute): from a match you play that is a quit. */
  toTitle(min: number) {
    if (this.at === 'match') this.quit('title', min);
    this.match.on = false;
  }
  hero(id: string) { this.track('hero', { hero: id }); }
  army(id: string) { this.track('army', { army: id }); }
  /** Your match is decided for you (won, or your base fell): once a match. */
  end(won: boolean, secs: number, army: string | null, hero: string | null, tier: number) {
    if (!this.match.on || this.match.ended) return;
    this.match.ended = true;
    this.track('end', { result: won ? 'win' : 'loss', secs: Math.round(secs), army: army ?? 'none', hero: hero ?? 'none', tier });
    this.frameRate();
    this.flush();
  }
  /** A frame-rate reading (main.ts takes one every half second); kept only while your match is on screen and running. */
  fps(fps: number, live: boolean) { if (live && this.match.on && !this.match.ended && this.match.fps.length < 20000) this.match.fps.push(fps); }
  /** The page is going away (`closed`: pagehide) or out of sight (`hidden`: a phone's home swipe or app switch, after
   *  which the system may kill the tab with no pagehide, Flash F1): where the player was, and the frame rate so far.
   *  Once per sighting: hidden then closed is one leave, and back() re-arms it. */
  leave(min: number, how: 'hidden' | 'closed') {
    if (this.left) return;
    this.left = true;
    this.quit(how, min);
    this.flush();
  }
  /** The page is in sight again after a leave: a `back` at the same place, so scripts/stats.ts counts leaves net of
   *  returns (no id needed to pair them). */
  back() {
    if (!this.left) return;
    this.left = false;
    this.track('back', { at: this.at });
  }
  /** An uncaught error: message, file and line only, three different ones a visit, benign notices skipped. */
  error(msg: unknown, src?: string, line?: number) {
    const m = String(msg ?? '').slice(0, 160);
    if (!m || BENIGN.some((re) => re.test(m)) || this.errors.has(m) || this.errors.size >= 3) return;
    this.errors.add(m);
    this.track('error', { msg: m, src: (src ?? '').split('/').pop()?.split('?')[0].slice(0, 60) ?? '', ...(Number.isInteger(line) ? { line: line! } : {}) });
  }

  private quit(to: 'closed' | 'hidden' | 'title', min: number) {
    const inMatch = this.at === 'match' && this.match.on && !this.match.ended;
    this.track('quit', { at: this.at, to, ...(inMatch ? { min: Math.max(0, Math.floor(min)) } : {}) });
    if (inMatch) this.frameRate();
    if (inMatch && to === 'title') this.match.ended = true; // hidden or closed: it may come back and go on
  }
  /** The frame rate since the last report as two buckets, its median and its slowest tenth (a match that went out of
   *  sight and came back reports each stretch). */
  private frameRate() {
    const f = this.match.fps.splice(0).sort((a, b) => a - b);
    if (f.length < 4) return; // under two seconds of play says nothing
    this.track('fps', { mid: fpsBucket(f[f.length >> 1]), low: fpsBucket(f[Math.floor(f.length * 0.1)]), input: this.input });
  }
}

/** The page's sender: a beacon to the same origin, fetch with keepalive where beacons are missing. */
export function beacon(body: string) {
  if (navigator.sendBeacon?.(STATS_PATH, new Blob([body], { type: 'text/plain' }))) return;
  fetch(STATS_PATH, { method: 'POST', body, keepalive: true, headers: { 'content-type': 'text/plain' } }).catch(() => {});
}
